/**
 * Configurações → Etiquetas.
 *
 * ── Por que criar etiqueta é um lugar, e não um campo de texto na conversa ─
 *
 * Medido contra o motor em 01/09/2026: colar um título inédito numa conversa
 * responde 200 e o catálogo da conta NÃO cresce. A etiqueta passaria a existir
 * só naquela conversa — sem cor, e ausente de qualquer lista construída do
 * catálogo. Funcionaria hoje e sumiria de todo lugar que importa depois.
 *
 * E há a razão de produto: etiqueta nova muda o vocabulário da revenda
 * inteira. Cada vendedor criando a sua produziria em um mês `quente`,
 * `lead-quente` e `cliente-quente` — três nomes para a mesma ideia, e nenhum
 * servindo para filtrar.
 *
 * Por isso o catálogo mora aqui, com quem administra. Aplicar a etiqueta numa
 * conversa é outro gesto, e esse é de quem atende.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, useCatalogoDeEtiquetas } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { chaves } from '@/shared/api/chaves';
import { Botao, Campo, Cartao, Etiqueta } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';

/**
 * As cores oferecidas.
 *
 * Uma paleta fechada, e não um seletor livre: cor escolhida à mão vira
 * pastilha ilegível — amarelo claro sobre branco — e ninguém percebe até
 * alguém reclamar que não consegue ler. Estas têm contraste conferido contra
 * o fundo da bolha e o da fila.
 */
const CORES = [
  { hex: '#1F93FF', nome: 'azul' },
  { hex: '#7C3AED', nome: 'roxo' },
  { hex: '#059669', nome: 'verde' },
  { hex: '#DC2626', nome: 'vermelho' },
  { hex: '#EA580C', nome: 'laranja' },
  { hex: '#CA8A04', nome: 'amarelo' },
  { hex: '#0891B2', nome: 'ciano' },
  { hex: '#DB2777', nome: 'rosa' },
  { hex: '#4B5563', nome: 'cinza' },
] as const;

export function SecaoEtiquetas(): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const { data, isPending, isError } = useCatalogoDeEtiquetas();

  const [titulo, definirTitulo] = useState('');
  const [cor, definirCor] = useState<string>(CORES[0].hex);

  const criar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.criarEtiqueta({ corpo: { titulo: titulo.trim(), cor } });
    },
    onSuccess: () => {
      definirTitulo('');
      void cache.invalidateQueries({ queryKey: chaves.catalogoDeEtiquetas() });
      // A fila e a conversa pintam as pastilhas com este catálogo: sem
      // invalidá-las, a etiqueta nova apareceria cinza até alguém recarregar.
      void cache.invalidateQueries({ queryKey: chaves.inbox() });
    },
  });

  /*
   * A conferência do formato acontece ANTES de enviar, e repete a do contrato.
   *
   * Não é redundância: é a diferença entre a pessoa saber enquanto digita e
   * descobrir depois de clicar. O servidor continua sendo a barreira — a
   * daqui só evita a viagem.
   */
  const normalizado = titulo.trim().toLowerCase();
  const formatoOk = /^[a-z0-9à-ÿ]+(?:[-_][a-z0-9à-ÿ]+)*$/.test(normalizado);
  const jaExiste = (data?.itens ?? []).some((e) => e.titulo.toLowerCase() === normalizado);
  const podeCriar = normalizado !== '' && formatoOk && !jaExiste && !criar.isPending;

  return (
    <section className="admin__secao" aria-label="Etiquetas">
      <p className="admin__nota">
        O vocabulário desta organização. As etiquetas criadas aqui ficam disponíveis em todas as
        conversas, com a cor escolhida.
      </p>

      {isPending && <p>Buscando as etiquetas…</p>}
      {isError && <Faixa tom="bloqueado">Não foi possível buscar as etiquetas.</Faixa>}

      {data && data.itens.length === 0 && (
        <p className="admin__nota">
          Nenhuma etiqueta ainda. Crie a primeira abaixo algo como <code>novo-lead</code> ou{' '}
          <code>test-drive</code>.
        </p>
      )}

      {data && data.itens.length > 0 && (
        <ul className="admin__catalogo">
          {data.itens.map((etiqueta) => (
            <li key={etiqueta.titulo}>
              {/*
                Aqui a etiqueta é o ASSUNTO da tela, então vai no tamanho
                padrão — nas linhas de fila e de clientes ela vai `--miuda`.

                As três declarações de `style` que moravam aqui saíram: a folha
                do componente deriva lombada e fundo de `--cor`, e esta cópia
                era uma das que envelheciam sozinhas.
              */}
              <Etiqueta nome={etiqueta.titulo} cor={etiqueta.cor} />
            </li>
          ))}
        </ul>
      )}

      <Cartao
        como="form"
        variante="folgado"
        parte="admin__form"
        onSubmit={(evento: FormEvent) => {
          evento.preventDefault();
          if (podeCriar) criar.mutate();
        }}
      >
        {/*
          O aviso aparece enquanto digita, e diz o que fazer — não só que está
          errado. "Use minúsculas e hífen" resolve; "formato inválido" manda
          adivinhar. Vive no campo, que já liga `aria-invalid` e a descrição.
        */}
        {/*
          Nome e cor na MESMA fileira: são as duas metades de uma decisão só, e
          empilhadas a pessoa escolhia a cor com o nome já fora da vista.
        */}
        <div className="admin__form-linha">
          <Campo
            rotulo="Nome"
            type="text"
            value={titulo}
            maxLength={40}
            placeholder="novo-lead"
            onChange={(e) => definirTitulo(e.currentTarget.value)}
            erro={
              normalizado !== '' && !formatoOk
                ? 'Use minúsculas, sem espaço — separe com hífen, como novo-lead.'
                : jaExiste
                  ? 'Esta etiqueta já existe.'
                  : undefined
            }
          />

          <fieldset className="admin__cores">
            <legend>Cor</legend>
            {CORES.map((c) => (
              <label key={c.hex} title={c.nome}>
                <input
                  type="radio"
                  name="cor"
                  value={c.hex}
                  checked={cor === c.hex}
                  onChange={() => definirCor(c.hex)}
                />
                {/* O nome da cor fica no rótulo lido pelo leitor de tela: uma
                    bolinha sem texto é invisível para quem não vê a cor. */}
                <span className="admin__bolinha" style={{ background: c.hex }} />
                <span className="visualmente-oculto">{c.nome}</span>
              </label>
            ))}
          </fieldset>
        </div>

        {criar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(criar.error, { generica: 'Não foi possível criar a etiqueta.' })}
          </Faixa>
        )}

        <Botao
          type="submit"
          variante="primario"
          desabilitado={!podeCriar}
          motivo={criar.isPending ? 'Criando…' : 'Preencha o nome da etiqueta.'}
        >
          {criar.isPending ? 'Criando…' : 'Criar etiqueta'}
        </Botao>
      </Cartao>
    </section>
  );
}
